export const TIMER_MODES = Object.freeze({
  OFF: 'off',
  FIXED: 'fixed',
  PER_ITEM: 'perItem'
});

export const MAXIMUM_OVERALL_TIME_SECONDS = 8 * 60 * 60;

export const boundedMinimumSeconds = (value) => Math.max(
  0,
  Math.min(300, Number.parseInt(value, 10) || 0)
);

export const boundedOverallTimeSeconds = (value) => Math.max(
  0,
  Math.min(MAXIMUM_OVERALL_TIME_SECONDS, Number.parseInt(value, 10) || 0)
);

export const overallTimeMinutes = (seconds) => Math.ceil(
  boundedOverallTimeSeconds(seconds) / 60
);

export const normalizeOverallTimeStatus = (raw = {}, fallbackLimitSeconds = 0) => ({
  limitSeconds: boundedOverallTimeSeconds(
    raw.overall_time_limit_seconds ?? raw.limitSeconds ?? fallbackLimitSeconds
  ),
  deadlineAt: raw.overall_deadline_at ?? raw.deadlineAt ?? null,
  remainingSeconds: boundedOverallTimeSeconds(
    raw.overall_remaining_seconds ?? raw.remainingSeconds
      ?? raw.overall_time_limit_seconds ?? raw.limitSeconds ?? fallbackLimitSeconds
  ),
  expired: Boolean(raw.overall_time_expired ?? raw.expired),
  serverTime: raw.server_time ?? raw.serverTime ?? null,
});

export const normalizeTimerMode = (value, defaultSeconds = 0) => {
  const key = String(value ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (!key) return boundedMinimumSeconds(defaultSeconds) > 0
    ? TIMER_MODES.FIXED
    : TIMER_MODES.OFF;
  if (['off', 'none', 'disabled'].includes(key)) return TIMER_MODES.OFF;
  if (['fixed', 'sameforall', 'allitems'].includes(key)) return TIMER_MODES.FIXED;
  if (['peritem', 'questionspecific', 'question'].includes(key)) return TIMER_MODES.PER_ITEM;
  return boundedMinimumSeconds(defaultSeconds) > 0 ? TIMER_MODES.FIXED : TIMER_MODES.OFF;
};

export const normalizeTimingPolicy = (rawPolicy, fallback = {}) => {
  let parsed = rawPolicy;
  if (typeof parsed === 'string' && parsed.trim()) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = null;
    }
  }
  const defaultSeconds = boundedMinimumSeconds(
    parsed?.defaultSeconds ?? fallback.defaultSeconds
  );
  const mode = normalizeTimerMode(parsed?.mode ?? fallback.mode, defaultSeconds);
  const questions = {};
  if (mode === TIMER_MODES.PER_ITEM && parsed?.questions
      && typeof parsed.questions === 'object' && !Array.isArray(parsed.questions)) {
    for (const [key, seconds] of Object.entries(parsed.questions)) {
      if (/^[1-9]\d*$/.test(key)) questions[key] = boundedMinimumSeconds(seconds);
    }
  }
  return {
    version: 1,
    mode,
    defaultSeconds: mode === TIMER_MODES.OFF ? 0 : defaultSeconds,
    questions
  };
};

export const resolveQuestionMinimum = (rawPolicy, question, fallbackSeconds = 0) => {
  const policy = normalizeTimingPolicy(rawPolicy, { defaultSeconds: fallbackSeconds });
  if (policy.mode === TIMER_MODES.OFF) return 0;
  if (policy.mode === TIMER_MODES.PER_ITEM) {
    const key = String(question?.ItemNumber ?? question ?? '');
    if (Object.hasOwn(policy.questions, key)) return boundedMinimumSeconds(policy.questions[key]);
    if (Number.isInteger(question?.Timing?.minimumSeconds)) {
      return boundedMinimumSeconds(question.Timing.minimumSeconds);
    }
  }
  return boundedMinimumSeconds(policy.defaultSeconds || fallbackSeconds);
};
